import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getMe, updateUserSettings } from "@/api/client";
import { getApiErrorMessage } from "@/lib/api";
import { isLanguage, setLanguage, type Language } from "@/lib/i18n";
import { useUserSettings } from "./useUserSettings";

// For logged in users the language saved in user_settings wins over the device's
// saved choice and the browser language.
export function useSyncLanguageFromSettings() {
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const { userSettings } = useUserSettings(!!me);
  const language = userSettings?.language;

  useEffect(() => {
    if (isLanguage(language)) setLanguage(language);
  }, [language]);
}

// Switches the language right away and saves it to user_settings silently
// (no "Settings saved" toast), sharing the ["userSettings"] cache.
export function useChangeUserLanguage() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: updateUserSettings,
    onSuccess: (data) => queryClient.setQueryData(["userSettings"], data),
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return (language: Language) => {
    setLanguage(language);
    mutation.mutate({ language });
  };
}
